/* eslint-disable arrow-body-style */
import { Box, Button, CircularProgress, Grid2, Typography, useTheme } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import JsxParser from 'react-jsx-parser';
import { useTranslation } from 'react-i18next';
import { getCheckoutByIdGql } from '../gql';
import LinkedStepper from './LinkedStepper';
import Tracking from '../../../components/Tracking';
import ExternalLink from '../../../components/ExternalLink';
import useIsIframe from '../../../hooks/useIsIframe';

export default function SuccessSummary({ details }) {
  const { t } = useTranslation('checkout');
  const params = useParams();
  const isIframe = useIsIframe();
  const theme = useTheme();

  const { data, loading } = useQuery(getCheckoutByIdGql, {
    variables: {
      slug: params.slug,
      externalId: params.externalId,
    },
  });

  // eslint-disable-next-line no-unused-vars
  const checkout = useMemo(() => data?.getCheckoutById, [data?.getCheckoutById]);
  const rsvpInviteUrl = checkout?.rsvpInviteUrl;
  const paymentStatus = checkout?.paymentStatus;
  const linkedStepperConfig = details?.event?.frontendSettings?.stepperConfig;
  const isLinkedFlow = !!details?.event?.frontendSettings?.isEcommerceLinked && !!linkedStepperConfig;

  const linkedSteps = useMemo(() => {
    const validSteps = Array.isArray(linkedStepperConfig?.steps) ? [...linkedStepperConfig.steps] : [];
    if (!validSteps.length) return null;
    return validSteps
      .sort((a, b) => (a.globalIndex || 0) - (b.globalIndex || 0))
      .map((step) => ({
        id: step.id,
        owner: step.owner,
        label: step.id === 'summary'
          ? t('steps.payment')
          : (step.title || step.id),
        globalIndex: step.globalIndex,
      }));
  }, [linkedStepperConfig?.steps, t]);

  const activeLinkedStepId = useMemo(() => {
    const paymentStep = (linkedSteps || []).find((step) => step.id === 'summary' && step.owner === 'ecommerce');
    if (paymentStep) return paymentStep.id;
    return (linkedSteps || []).find((step) => step.owner === 'ecommerce')?.id;
  }, [linkedSteps]);

  useEffect(() => {
    if (!rsvpInviteUrl) return undefined;
    window.location.href = rsvpInviteUrl;
    return undefined;
  }, [rsvpInviteUrl]);

  if (loading) {
    return (
      <Grid2 sx={{ width: '100%', textAlign: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Grid2>
    );
  }

  return (
    <Box
      sx={{
        p: 4,
        maxWidth: 800,
        mx: 'auto',
        mb: 4,
        ...(isIframe ? {} : {
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 3,
        }),
        overflow: 'visible',
      }}
    >
      {isLinkedFlow && linkedSteps?.length > 0 ? (
        <LinkedStepper
          linkedSteps={linkedSteps}
          activeStepId={activeLinkedStepId}
          theme={theme}
        />
      ) : null}

      <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', marginBottom: 3 }}>
        Vielen Dank für Ihre Bestellung
      </Typography>
      {details?.event?.frontendSettings?.checkoutSuccessText?.trim() ? (
        <JsxParser
          components={{ ExternalLink, Link: ExternalLink, a: ExternalLink }}
          jsx={details.event.frontendSettings.checkoutSuccessText}
          bindings={{ checkout }}
        />
      ) : (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Ihre Bestellnummer lautet <b>{checkout?.orderNumber}</b>. Wir haben Ihnen die Bestellbestätigung per E-Mail zugesandt.
          </Typography>
          {paymentStatus === 'succeeded' ? (
            <Typography variant="subtitle1">
              Ihre Zahlung wurde erfolgreich abgeschlossen.
            </Typography>
          ) : (
            <Typography variant="subtitle1">
              Ihre Bestellung wurde aufgenommen. Der Zahlungsstatus wird noch verarbeitet.
            </Typography>
          )}
        </>
      )}
      {rsvpInviteUrl ? (
        <Box sx={{ marginTop: 2, textAlign: 'center' }}>
          <Button href={rsvpInviteUrl} variant="contained">Weiter zu RSVP</Button>
        </Box>
      ) : null}
      <Box sx={{ marginTop: 2, textAlign: 'center' }}>
        <Button href={`/${params.slug}/checkout`} variant="contained">Weitere Bestellung aufgeben</Button>
      </Box>
      <Tracking details={details} checkout={checkout} />
    </Box>
  );
}
